// src/shared/pricing.js
//
// THE pricing engine — single source of truth for every price the app computes.
//
// Used by BOTH:
//   • src/SignPage.js        (what the client sees while signing)
//   • routes/documents.js    (what the signed PDF says they agreed to)
//
// Those two MUST agree: the number on the sign page and the number on the executed
// PDF are the same legal promise. This module exists because the logic used to be
// copy-pasted in both places and drifted — a fix applied to one copy missed the
// other, and a declined core product kept billing. Never fork this file; change it
// here and both sides move together.
//
// CommonJS on purpose: the Node server `require()`s it and the CRA/webpack client
// `import`s it. It lives under src/ because CRA refuses to compile imports from
// outside src/. Keep it dependency-free and free of React/Node-only APIs.

// "$1,500" -> 1500 · "" / "N/A" / "Included" -> null (no numeric contribution)
function parsePrice(str) {
  if (!str) return null;
  const n = parseFloat(String(str).replace(/[$,]/g, '').trim());
  return isNaN(n) ? null : n;
}

// Collapse the flat map of signing "spots" into per-row pricing state:
//   rowId -> { qty, unlimited, selected }
// qty spots carry the entered number; the matching "unlimited" spot carries
// isUnlimited; a plain initial spot marks the row as selected (accepted).
function buildPricingState(spots) {
  const st = {};
  if (!spots) return st;
  for (const s of Object.values(spots)) {
    if (!s || !s.rowId) continue;
    const e = st[s.rowId] || (st[s.rowId] = {});
    if (s.type === 'qty') e.qty = s.value;
    if (s.isUnlimited) e.unlimited = !!s.filled;
    else if (s.type === 'initial') e.selected = !!s.filled;
  }
  return st;
}

// Numeric line price for one row.
// Rows with a structured `pricing` block compute from it; every other row falls
// back to its static `price` string. Returns a number, or null when unpriced.
function rowLinePrice(row, state) {
  const p = row.pricing;
  if (!p) return parsePrice(row.price);
  const base = p.base || 0;
  if (state && state.unlimited && p.unlimited != null) return base + p.unlimited;
  const entered = Math.max(0, Math.floor(Number(state && state.qty)) || 0);
  // minUnits: always bill at least this many at unit price; bundling applies above it.
  const minU = Math.max(0, p.minUnits || 0);
  const qty = Math.max(entered, minU);
  let amt = base;
  if (p.bundleSize && p.bundlePrice != null) {
    const extra = qty - minU;
    amt += minU * (p.unit || 0)
         + Math.floor(extra / p.bundleSize) * p.bundlePrice
         + (extra % p.bundleSize) * (p.unit || 0);
  } else {
    amt += qty * (p.unit || 0);
  }
  return amt;
}

// Sum one year column of a dual-year vendor table ("price" = year 1, "price2" =
// subsequent year). Mirrors calcTotal's selection gate.
function calcTotalYear(rows, stateByRowId, year) {
  const st = stateByRowId || {};
  const field = year === 2 ? 'price2' : 'price';
  const sum = (rows || [])
    .filter(r => !r.isTotal)
    .reduce((acc, r) => {
      const n = parsePrice(r[field]);
      if (n === null) return acc;
      if (r.initial && !r.pricing && !(st[r.id] && st[r.id].selected)) return acc;
      return acc + n;
    }, 0);
  return sum > 0 ? `$${sum.toLocaleString('en-US')}` : null;
}

// Grand total for a single-year table.
function calcTotal(rows, stateByRowId) {
  const st = stateByRowId || {};
  const sum = (rows || [])
    .filter(r => !r.isTotal)
    .reduce((acc, r) => {
      const n = rowLinePrice(r, st[r.id]);
      if (n === null) return acc;
      // ANY initial row (core product or optional add-on) only counts once the client
      // initials it — cores are declinable, so a declined/blank core must not bill.
      // Static non-initial rows and quantity/pricing rows always count (qty rows are $0 until filled).
      if (r.initial && !r.pricing && !(st[r.id] && st[r.id].selected)) return acc;
      return acc + n;
    }, 0);
  return sum > 0 ? `$${sum.toLocaleString('en-US')}` : null;
}

// How a row's Price cell should read. Pricing rows always show a computed amount
// ($0 until a quantity/unlimited is chosen); static rows show as authored, except
// an unset total row which falls back to the computed grand total.
function rowDisplayPrice(row, state, autoTotal) {
  if (row.pricing) {
    const lp = rowLinePrice(row, state);
    return lp != null ? `$${lp.toLocaleString('en-US')}` : '';
  }
  const storedPrice = row.price ? row.price.trim() : '';
  const isUnset = !storedPrice || storedPrice === '$';
  return row.isTotal && isUnset && autoTotal ? autoTotal : storedPrice;
}

// Stable id linking a bank-of-hours section to its quantity spot across
// buildSpots, the sign-page renderer, the pricing summary, and the signed PDF.
function bankRowId(section) {
  return 'bank__' + ((section && (section._srcKey || section._module)) || 'mod');
}

// A `pricing_total` section ("Software Grand Total: $") sums the pricing tables
// that come BEFORE it — and after any previous pricing_total, so a document can
// carry more than one grand total without double counting.
// Bank-of-hours is deliberately excluded: it is labor, billed separately from
// software pricing. Returns { [sectionIndex]: amount }.
function grandTotalsByIndex(sections, stateByRowId) {
  const out = {};
  let running = 0;
  (sections || []).forEach((s, i) => {
    if (!s) return;
    if (s.type === 'pricing_table') {
      const rows = s.rows || [];
      running += s.dualYear
        ? (parsePrice(calcTotalYear(rows, stateByRowId, 1)) || 0)
        : (parsePrice(calcTotal(rows, stateByRowId)) || 0);
    } else if (s.type === 'pricing_total') {
      out[i] = running;
      running = 0;
    }
  });
  return out;
}

// Render a pricing_total's text with its computed amount. Templates author these
// as "Software Grand Total: $" — the trailing $ is the placeholder to fill.
function fillPricingTotal(content, amount) {
  const base = String(content == null ? '' : content);
  const txt = amount > 0 ? `$${Number(amount).toLocaleString('en-US')}` : '';
  if (/\$\s*$/.test(base)) return base.replace(/\$\s*$/, '') + (txt || '$');
  return txt ? `${base} ${txt}` : base;
}

// Pricing state for an agreement nobody has signed yet: every declinable row
// counted as accepted, every quantity row at its floor. This is the "as offered"
// figure — what the deal is worth if the client takes the quote as written.
// Once spots exist, use buildPricingState instead: that is what they actually
// agreed to, and the two will differ whenever a core product is declined.
function offeredPricingState(sections) {
  const st = {};
  for (const s of sections || []) {
    if (!s || s.type !== 'pricing_table') continue;
    for (const r of s.rows || []) {
      if (r && r.id && r.initial) st[r.id] = { selected: true };
    }
  }
  return st;
}

// Total contract value for the whole document: every pricing table plus any
// prepaid support hours. Deliberately NOT the same number as grandTotalsByIndex —
// that renders the document's "Software Grand Total", which excludes labor and
// restarts at each pricing_total. This is the one figure the CRM wants for the
// deal: everything the client is committing to pay.
//
// Dual-year tables contribute year 1 — the signed commitment; year 2 renews.
function contractTotal(sections, stateByRowId) {
  const st = stateByRowId || {};
  let sum = 0;
  for (const s of sections || []) {
    if (!s) continue;
    if (s.type === 'pricing_table') {
      const rows = s.rows || [];
      sum += (s.dualYear ? parsePrice(calcTotalYear(rows, st, 1)) : parsePrice(calcTotal(rows, st))) || 0;
    } else if (s.type === 'bank_of_hours') {
      const hours = Math.max(0, Number((st[bankRowId(s)] || {}).qty) || 0);
      sum += hours * (s.rate || 180);
    }
  }
  return sum;
}

module.exports = {
  parsePrice,
  buildPricingState,
  offeredPricingState,
  contractTotal,
  rowLinePrice,
  calcTotalYear,
  calcTotal,
  rowDisplayPrice,
  bankRowId,
  grandTotalsByIndex,
  fillPricingTotal,
};
